/**
 * ApiKeyManager.tsx — API keys for MCP clients and scripts (Connections page).
 *
 * The scope shown is the scope the server enforces
 * (backend/internal/apikeys/scope.go): only a key with mcp:read and without
 * mcp:write is read-only; empty or unrecognised scopes act with the
 * read+write default. project_ids null = every project the owner can open;
 * a list narrows it, and is always intersected with the owner's current
 * access. The raw token is shown once, on creation; stored keys show only
 * their prefix. There is no rotate endpoint: rotation creates a replacement
 * with the same scope (minus projects the owner can no longer open) and the
 * remaining lifetime, then revokes the old key.
 */
import React, { useCallback, useEffect, useId, useState } from 'react';
import { Key, CheckCircle, Check, Plus } from 'lucide-react';
import { apiClient, getErrorMessage } from '../packages/api-client';
import { getProjects } from '../packages/api-client/pm-api';
import { Dialog, StateView } from '../packages/ui';
import { mcpJsonSnippet } from './mcpSetup';

interface ApiKey {
    id: string;
    name: string;
    prefix: string;
    scopes: string[];
    project_ids: string[] | null;
    created_at: string;
    last_used_at?: string;
    expires_at?: string;
}

interface CreatedKey extends ApiKey {
    token: string; // raw token — only returned on creation
}

interface CreateBody {
    name: string;
    scopes: string[];
    project_ids?: string[];
    expires_in?: string; // Go duration, e.g. "720h" (apikeys.CreateInput.ExpiresIn)
}

type ProjectOption = { id: string; name: string };

const READ = 'mcp:read';
const WRITE = 'mcp:write';

/** Mirrors apikeys.Allows: read-only only when mcp:read is present without mcp:write. */
function isReadOnly(scopes: string[] | null): boolean {
    const known = (scopes ?? []).filter((s) => s === READ || s === WRITE);
    return known.length > 0 && !known.includes(WRITE);
}

const enforcedScopes = (scopes: string[] | null) => (isReadOnly(scopes) ? [READ] : [READ, WRITE]);
const accessLabel = (scopes: string[] | null) => (isReadOnly(scopes) ? 'Read only' : 'Read and write');

function relDate(iso?: string) {
    if (!iso) return 'Never';
    const diff = Math.round((Date.now() - new Date(iso).getTime()) / 86400000);
    if (diff === 0) return 'Today';
    if (diff === 1) return 'Yesterday';
    return `${diff}d ago`;
}

// ponytail: pages through /projects 100 at a time; fine for hundreds of projects,
// swap for a search-as-you-type picker if a tenant has thousands.
async function loadAllProjects(): Promise<ProjectOption[]> {
    const out: ProjectOption[] = [];
    for (let page = 1; page <= 100; page++) {
        const res = await getProjects(page, 100);
        const rows = res.data ?? [];
        out.push(...rows.map((p) => ({ id: p.id, name: p.name })));
        if (rows.length < 100 || out.length >= (res.meta?.total ?? 0)) break;
    }
    return out;
}

function projectScopeLabel(ids: string[] | null, projects: ProjectOption[] | null): string {
    if (ids === null) return 'All projects you can open';
    if (!projects) return `${ids.length} selected project${ids.length === 1 ? '' : 's'}`;
    const names = ids.map((id) => projects.find((p) => p.id === id)?.name).filter((n): n is string => !!n);
    const gone = ids.length - names.length;
    const parts = names.length ? [`Only ${names.join(', ')}`] : ['No project you can open'];
    if (gone) parts.push(`${gone} project${gone === 1 ? '' : 's'} you can no longer open`);
    return parts.join(' · ');
}

/**
 * The replacement a rotation would create, or why it can't. The server refuses
 * the whole request if project_ids names a project the owner can no longer
 * open, so only still-accessible ones are carried; the replacement keeps the
 * old key's remaining lifetime (rounded up to whole seconds).
 */
type RotationPlan = { body: CreateBody; dropped: number } | { blocked: string };

function rotationPlan(k: ApiKey, projects: ProjectOption[] | null, projectsError: boolean): RotationPlan {
    const body: CreateBody = { name: k.name, scopes: enforcedScopes(k.scopes) };
    let dropped = 0;
    if (k.project_ids !== null) {
        if (projectsError) return { blocked: "Couldn't load your projects to check this key's scope." };
        if (!projects) return { blocked: "Checking this key's projects…" };
        const kept = k.project_ids.filter((id) => projects.some((p) => p.id === id));
        if (kept.length === 0) return { blocked: "This key's projects are no longer accessible; create a new key." };
        body.project_ids = kept;
        dropped = k.project_ids.length - kept.length;
    }
    if (k.expires_at) {
        const ms = new Date(k.expires_at).getTime() - Date.now();
        if (!(ms > 0)) return { blocked: 'This key has expired; create a new key.' };
        body.expires_in = `${Math.ceil(ms / 1000)}s`;
    }
    return { body, dropped };
}

type Pending = { kind: 'revoke' | 'rotate'; key: ApiKey } | null;

export function ApiKeyManager() {
    const [keys, setKeys] = useState<ApiKey[] | null>(null);
    const [loadError, setLoadError] = useState(false);
    const [name, setName] = useState('');
    const [access, setAccess] = useState<'' | 'read' | 'write'>('');
    const [projectMode, setProjectMode] = useState<'' | 'all' | 'selected'>('');
    const [selected, setSelected] = useState<string[]>([]);
    const [projects, setProjects] = useState<ProjectOption[] | null>(null);
    const [projectsError, setProjectsError] = useState(false);
    const [creating, setCreating] = useState(false);
    const [createdKey, setCreatedKey] = useState<CreatedKey | null>(null);
    const [copied, setCopied] = useState<'token' | 'snippet' | null>(null);
    const [pending, setPending] = useState<Pending>(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [notice, setNotice] = useState<string | null>(null);
    const revealId = useId();

    const loadKeys = useCallback(() => {
        setLoadError(false);
        setKeys(null);
        apiClient.get<{ data: ApiKey[] }>('/api-keys')
            .then((r) => setKeys(r.data.data ?? []))
            .catch(() => setLoadError(true));
    }, []);
    useEffect(loadKeys, [loadKeys]);

    // Project names are needed for the picker and for scoped keys' labels.
    const needProjects = projectMode === 'selected' || !!keys?.some((k) => k.project_ids !== null);
    useEffect(() => {
        if (!needProjects || projects || projectsError) return;
        loadAllProjects().then(setProjects).catch(() => setProjectsError(true));
    }, [needProjects, projects, projectsError]);

    async function create(body: CreateBody): Promise<CreatedKey> {
        const { data } = await apiClient.post<{ data: CreatedKey }>('/api-keys', body);
        return data.data;
    }

    function show(k: CreatedKey, replacing?: string) {
        setCreatedKey(k);
        const { token: _token, ...row } = k;
        setKeys((prev) => [row, ...(prev ?? []).filter((x) => x.id !== replacing)]);
    }

    async function handleCreate(e: React.FormEvent) {
        e.preventDefault();
        setNotice(null);
        if (!name.trim()) return setError('Give this key a name so you can identify it later (e.g. "Agent — laptop").');
        if (!access) return setError('Choose what this key can do: read only, or read and write.');
        if (!projectMode) return setError('Choose which projects this key can reach.');
        if (projectMode === 'selected' && selected.length === 0) return setError('Select at least one project, or choose all projects.');
        const body: CreateBody = { name: name.trim(), scopes: access === 'read' ? [READ] : [READ, WRITE] };
        if (projectMode === 'selected') body.project_ids = selected;
        setCreating(true);
        setError(null);
        try {
            show(await create(body));
            setName('');
            setAccess('');
            setProjectMode('');
            setSelected([]);
        } catch (err) {
            setError(`Couldn't create the key: ${getErrorMessage(err)}`);
        } finally {
            setCreating(false);
        }
    }

    async function confirmPending() {
        if (!pending) return;
        const { kind, key } = pending;
        setBusy(true);
        setError(null);
        setNotice(null);
        try {
            if (kind === 'rotate') {
                const plan = rotationPlan(key, projects, projectsError);
                if ('blocked' in plan) {
                    setError(plan.blocked);
                    return;
                }
                let fresh: CreatedKey;
                try {
                    fresh = await create(plan.body);
                } catch (err) {
                    setError(`Couldn't create the replacement key: ${getErrorMessage(err)}. The old key still works.`);
                    return;
                }
                show(fresh);
                try {
                    await apiClient.delete(`/api-keys/${key.id}`);
                } catch (err) {
                    setError(`The replacement was created, but the old key couldn't be revoked: ${getErrorMessage(err)}. Revoke it below.`);
                    return;
                }
                setKeys((prev) => (prev ?? []).filter((x) => x.id !== key.id));
                setNotice(`Old “${key.name}” key revoked. Requests using it are refused from the next request, and within 10 seconds on every server.`);
            } else {
                try {
                    await apiClient.delete(`/api-keys/${key.id}`);
                } catch (err) {
                    setError(`Couldn't revoke “${key.name}”: ${getErrorMessage(err)}`);
                    return;
                }
                setKeys((prev) => (prev ?? []).filter((x) => x.id !== key.id));
                setNotice(`“${key.name}” revoked. Requests using it are refused from the next request, and within 10 seconds on every server.`);
            }
        } finally {
            setBusy(false);
            setPending(null);
        }
    }

    async function copy(value: string, field: 'token' | 'snippet') {
        try {
            await navigator.clipboard.writeText(value);
            setCopied(field);
            setTimeout(() => setCopied(null), 2000);
        } catch {
            setError('Copy was blocked by the browser — select the text and copy it manually.');
        }
    }

    const toggleProject = (id: string) =>
        setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-4)' }}>
            <div>
                <h2 style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', margin: 0, fontSize: 'var(--font-size-md)', fontWeight: 700 }}>
                    <Key size={18} aria-hidden="true" /> API keys
                </h2>
                <p style={{ marginTop: 4, fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>
                    Bearer tokens for MCP clients such as Claude Code, and for scripts. A key acts as you: it can never do more than you can right now, narrowed further by the access and projects you choose here.
                </p>
            </div>

            {error && (
                <div role="alert" style={{ background: 'rgba(239,68,68,0.08)', border: '1px solid var(--color-danger)', borderRadius: 'var(--radius-md)', padding: 'var(--space-3) var(--space-4)', fontSize: 'var(--font-size-sm)', color: 'var(--color-danger)' }}>
                    {error}
                </div>
            )}
            {notice && (
                <div role="status" style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)' }}>{notice}</div>
            )}

            {/* One-shot reveal: token + MCP snippet */}
            {createdKey && (
                <section aria-labelledby={revealId} style={{ background: 'rgba(34,197,94,0.08)', border: '1px solid var(--color-success)', borderRadius: 'var(--radius-md)', padding: 'var(--space-4)', display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                    <div id={revealId} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontWeight: 700, fontSize: 'var(--font-size-sm)', color: 'var(--color-success)' }}>
                        <CheckCircle size={16} aria-hidden="true" /> Copy your new key now — it won't be shown again.
                    </div>
                    <div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 4 }}>
                            Token for “{createdKey.name}” · {accessLabel(createdKey.scopes)} · {projectScopeLabel(createdKey.project_ids, projects)}
                        </div>
                        <div style={{ display: 'flex', gap: 'var(--space-2)', alignItems: 'center' }}>
                            <code style={{ flex: 1, fontSize: 12, background: 'var(--color-surface-2)', padding: '8px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border)', overflowX: 'auto', wordBreak: 'break-all' }}>{createdKey.token}</code>
                            <button type="button" className="btn btn-primary btn-sm" aria-label="Copy token" onClick={() => copy(createdKey.token, 'token')}>
                                {copied === 'token' ? <><Check size={14} aria-hidden="true" /> Copied</> : 'Copy'}
                            </button>
                        </div>
                    </div>
                    <div>
                        <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginBottom: 4 }}>
                            Claude Code: merge into <code>~/.claude.json</code>, restart Claude Code, then run <code>/mcp</code>.
                        </div>
                        <pre style={{ margin: 0, fontSize: 12, background: 'var(--color-surface-2)', padding: '10px 12px', borderRadius: 'var(--radius-sm)', border: '1px solid var(--color-border)', overflowX: 'auto', whiteSpace: 'pre-wrap' }}>
                            {mcpJsonSnippet(createdKey.token)}
                        </pre>
                        <div style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-2)' }}>
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => copy(mcpJsonSnippet(createdKey.token), 'snippet')}>
                                {copied === 'snippet' ? <><Check size={14} aria-hidden="true" /> Copied</> : 'Copy snippet'}
                            </button>
                            <button type="button" className="btn btn-ghost btn-sm" onClick={() => setCreatedKey(null)}>I've saved it</button>
                        </div>
                    </div>
                </section>
            )}

            {/* Create form */}
            <form onSubmit={handleCreate} noValidate style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
                <div>
                    <label htmlFor="api-key-name" style={{ display: 'block', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 600, marginBottom: 4 }}>
                        Key name
                    </label>
                    <input id="api-key-name" className="form-input" style={{ width: '100%' }} placeholder="e.g. Agent — laptop"
                        value={name} onChange={(e) => setName(e.target.value)} maxLength={64} required />
                </div>
                <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
                    <legend style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 600, marginBottom: 4 }}>Access</legend>
                    <label style={{ display: 'flex', gap: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>
                        <input type="radio" name="api-key-access" checked={access === 'read'} onChange={() => setAccess('read')} />
                        Read only — list and read; every change is refused
                    </label>
                    <label style={{ display: 'flex', gap: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>
                        <input type="radio" name="api-key-access" checked={access === 'write'} onChange={() => setAccess('write')} />
                        Read and write — also create and update work, as you could
                    </label>
                </fieldset>
                <fieldset style={{ border: 0, padding: 0, margin: 0 }}>
                    <legend style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 600, marginBottom: 4 }}>Projects</legend>
                    <label style={{ display: 'flex', gap: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>
                        <input type="radio" name="api-key-projects" checked={projectMode === 'all'} onChange={() => setProjectMode('all')} />
                        All projects you can open (now and later)
                    </label>
                    <label style={{ display: 'flex', gap: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>
                        <input type="radio" name="api-key-projects" checked={projectMode === 'selected'} onChange={() => setProjectMode('selected')} />
                        Only selected projects
                    </label>
                    {projectMode === 'selected' && (
                        <div style={{ marginTop: 'var(--space-2)', paddingLeft: 'var(--space-4)', maxHeight: 200, overflowY: 'auto' }}>
                            {projectsError ? (
                                <StateView state="error" message="Couldn't load your projects." onRetry={() => setProjectsError(false)} />
                            ) : !projects ? (
                                <StateView state="loading" label="Loading projects" lines={2} />
                            ) : projects.length === 0 ? (
                                <p style={{ fontSize: 'var(--font-size-sm)', color: 'var(--color-text-muted)', margin: 0 }}>You can't open any projects yet.</p>
                            ) : projects.map((p) => (
                                <label key={p.id} style={{ display: 'flex', gap: 'var(--space-2)', fontSize: 'var(--font-size-sm)' }}>
                                    <input type="checkbox" checked={selected.includes(p.id)} onChange={() => toggleProject(p.id)} />
                                    {p.name}
                                </label>
                            ))}
                        </div>
                    )}
                </fieldset>
                <div>
                    <button className="btn btn-primary btn-sm" style={{ display: 'inline-flex', alignItems: 'center', gap: 'var(--space-1)' }} type="submit" disabled={creating}>
                        {creating ? 'Creating…' : <><Plus size={14} aria-hidden="true" /> Create key</>}
                    </button>
                </div>
            </form>

            {/* Keys list */}
            {loadError ? (
                <StateView state="error" message="Couldn't load your API keys." onRetry={loadKeys} />
            ) : keys === null ? (
                <StateView state="loading" label="Loading API keys" lines={2} />
            ) : keys.length === 0 ? (
                <p style={{ color: 'var(--color-text-muted)', fontSize: 'var(--font-size-sm)', textAlign: 'center', padding: 'var(--space-4)', background: 'var(--color-surface-2)', borderRadius: 'var(--radius-md)', margin: 0 }}>
                    No API keys yet.
                </p>
            ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0, display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
                    {keys.map((k) => {
                        const plan = rotationPlan(k, projects, projectsError);
                        const blocked = 'blocked' in plan ? plan.blocked : null;
                        return (
                        <li key={k.id} aria-label={k.name} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-3)', padding: 'var(--space-3) var(--space-4)', background: 'var(--color-surface-2)', borderRadius: 'var(--radius-md)', border: '1px solid var(--color-border)', flexWrap: 'wrap' }}>
                            <div style={{ flex: 1, minWidth: 0 }}>
                                <div style={{ fontWeight: 600, fontSize: 'var(--font-size-sm)' }}>{k.name}</div>
                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text)', marginTop: 2 }}>
                                    <span>{accessLabel(k.scopes)}</span>{' · '}<span>{projectScopeLabel(k.project_ids, projects)}</span>
                                </div>
                                <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>
                                    <code>{k.prefix}…</code>
                                    {' · '}Created {relDate(k.created_at)}
                                    {' · '}Last used {relDate(k.last_used_at)}
                                    {k.expires_at ? ` · Expires ${new Date(k.expires_at).toLocaleDateString()}` : ''}
                                </div>
                                {blocked && (
                                    <div id={`rotate-why-${k.id}`} style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', marginTop: 2 }}>{blocked}</div>
                                )}
                            </div>
                            <button type="button" className="btn btn-ghost btn-sm" disabled={busy || !!blocked}
                                aria-describedby={blocked ? `rotate-why-${k.id}` : undefined}
                                onClick={() => setPending({ kind: 'rotate', key: k })}>Rotate</button>
                            <button type="button" className="btn btn-ghost btn-sm" style={{ color: 'var(--color-danger)' }} disabled={busy} onClick={() => setPending({ kind: 'revoke', key: k })}>Revoke</button>
                        </li>
                        );
                    })}
                </ul>
            )}

            <Dialog
                open={pending !== null}
                onClose={() => { if (!busy) setPending(null); }}
                title={pending ? `${pending.kind === 'rotate' ? 'Rotate' : 'Revoke'} “${pending.key.name}”?` : ''}
                footer={pending && (
                    <>
                        <button type="button" className="btn btn-secondary btn-md" disabled={busy} onClick={() => setPending(null)}>Cancel</button>
                        <button type="button" className={`btn btn-${pending.kind === 'rotate' ? 'primary' : 'danger'} btn-md`} disabled={busy} onClick={confirmPending}>
                            {pending.kind === 'rotate' ? 'Rotate key' : 'Revoke key'}
                        </button>
                    </>
                )}
            >
                {pending?.kind === 'rotate' ? (() => {
                    const plan = rotationPlan(pending.key, projects, projectsError);
                    const dropped = 'dropped' in plan ? plan.dropped : 0;
                    return (
                        <p>
                            A new key with the same access ({accessLabel(pending.key.scopes)}; {projectScopeLabel(pending.key.project_ids, projects)}) is created and shown once, then this key is revoked.
                            {dropped > 0 && ` ${dropped} project${dropped === 1 ? '' : 's'} you can no longer open ${dropped === 1 ? 'is' : 'are'} dropped from the new key.`}
                            {pending.key.expires_at && ` The new key expires when this one would (${new Date(pending.key.expires_at).toLocaleString()}).`}
                            {' '}Update every client that uses it. Requests with the old key are refused from the next request, and within 10 seconds on every server.
                        </p>
                    );
                })() : (
                    <p>Clients using this key stop working: requests with it are refused from the next request, and within 10 seconds on every server. This can't be undone.</p>
                )}
            </Dialog>
        </div>
    );
}
